import React from 'react';
import { Helmet } from 'react-helmet-async';

const TermsOfService: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service - FutureXSkills</title>
        <meta name="description" content="Terms of Service for FutureXSkills - Read our terms and conditions for using our educational platform and services." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://futurexskills.com/terms-of-service" />
      </Helmet>
      
      <div className="max-w-4xl mx-auto py-16 px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold gradient-text mb-4">Terms of Service</h1>
          <p className="text-gray-600">Last updated: November 6, 2025</p>
        </div>

        <div className="glass-card p-8 space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">1. Acceptance of Terms</h2>
            <div className="text-gray-700">
              <p>By accessing and using FutureXSkills website (futurexskills.com), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">2. Description of Service</h2>
            <div className="text-gray-700 space-y-4">
              <p>FutureXSkills provides:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li><strong>Educational Content:</strong> Information about AI, Machine Learning, and Data Engineering courses</li>
                <li><strong>Course Listings:</strong> Details about available courses on third-party platforms (primarily Udemy)</li>
                <li><strong>Blog Content:</strong> Educational articles and tutorials</li>
                <li><strong>Community Resources:</strong> Learning materials and career guidance</li>
                <li><strong>Referral Services:</strong> Links to external course platforms</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">3. User Responsibilities</h2>
            <div className="text-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Acceptable Use</h3>
              <p className="mb-4">You agree to use our website only for lawful purposes. You must not:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe on intellectual property rights</li>
                <li>Transmit harmful, offensive, or inappropriate content</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Use automated systems to scrape or collect data</li>
                <li>Interfere with the proper functioning of the website</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">4. Third-Party Course Platforms</h2>
            <div className="text-gray-700 space-y-4">
              <div className="bg-orange-500/10 p-4 rounded-lg border border-orange-500/20">
                <h3 className="text-lg font-semibold text-orange-600 mb-2">⚠️ Important Notice</h3>
                <p>FutureXSkills acts as an educational resource and referral service. When you enroll in courses through our links:</p>
                <ul className="list-disc list-inside ml-4 space-y-1 mt-2">
                  <li>You will be redirected to third-party platforms (e.g., Udemy)</li>
                  <li>Course purchases are subject to the third-party's terms and policies</li>
                  <li>We are not responsible for course content, quality, or delivery</li>
                  <li>Refunds and support are handled by the course platform</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">5. Intellectual Property</h2>
            <div className="text-gray-700 space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Our Content</h3>
              <p>All content on FutureXSkills, including but not limited to text, graphics, logos, images, and software, is the property of FutureXSkills and is protected by copyright and other intellectual property laws.</p>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4">User Content</h3>
              <p>By submitting content (comments, reviews, feedback), you grant us a non-exclusive, royalty-free license to use, modify, and display such content for educational and promotional purposes.</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">6. Disclaimers</h2>
            <div className="text-gray-700 space-y-4">
              <div className="bg-red-500/10 p-4 rounded-lg border border-red-500/20">
                <h3 className="text-lg font-semibold text-red-600 mb-2">Educational Purposes</h3>
                <p>Our content is for educational and informational purposes only. We do not guarantee:</p>
                <ul className="list-disc list-inside ml-4 space-y-1 mt-2">
                  <li>Employment outcomes or career advancement</li>
                  <li>Specific learning results or skill acquisition</li>
                  <li>Accuracy or completeness of all information</li>
                  <li>Availability or quality of third-party courses</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">7. Limitation of Liability</h2>
            <div className="text-gray-700">
              <p className="mb-4">To the maximum extent permitted by law, FutureXSkills shall not be liable for:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Any indirect, incidental, or consequential damages</li>
                <li>Loss of profits, data, or business opportunities</li>
                <li>Issues arising from third-party course platforms</li>
                <li>Technical problems or website downtime</li>
                <li>Actions of other users or external parties</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">8. Privacy and Data Protection</h2>
            <div className="text-gray-700">
              <p>Your privacy is important to us. Please review our <a href="/privacy-policy" className="text-blue-600 hover:text-blue-500 underline">Privacy Policy</a> to understand how we collect, use, and protect your information. By using our service, you consent to the collection and use of information in accordance with our Privacy Policy.</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">9. Affiliate Relationships</h2>
            <div className="text-gray-700">
              <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-500/20">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Transparency Notice</h3>
                <p>FutureXSkills may earn commissions from course enrollments through affiliate partnerships. This does not affect the price you pay for courses, and we only recommend courses we believe provide value to learners.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">10. Modifications to Terms</h2>
            <div className="text-gray-700">
              <p>We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting on the website. Your continued use of the service after such modifications constitutes acceptance of the updated terms.</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">11. Termination</h2>
            <div className="text-gray-700">
              <p>We may terminate or suspend access to our service immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms of Service.</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">12. Contact Information</h2>
            <div className="text-gray-700">
              <p className="mb-4">If you have any questions about these Terms of Service, please contact us:</p>
              <div className="bg-blue-500/10 p-4 rounded-lg">
                <p><strong>Email:</strong> <a href="mailto:futurexskill@gmail.com" className="text-blue-600 hover:text-blue-500">futurexskill@gmail.com</a></p>
                <p><strong>Website:</strong> <a href="https://futurexskills.com" className="text-blue-600 hover:text-blue-500">https://futurexskills.com</a></p>
                <p><strong>Response Time:</strong> We aim to respond within 48 hours</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">13. Governing Law</h2>
            <div className="text-gray-700">
              <p>These terms shall be interpreted and governed in accordance with applicable laws. Any disputes arising under these terms shall be subject to the exclusive jurisdiction of the competent courts.</p>
            </div>
          </section>
        </div>

        <div className="text-center mt-8">
          <p className="text-gray-600 text-sm">
            By using FutureXSkills, you acknowledge that you have read, understood, and agree to these Terms of Service.
          </p>
        </div>
      </div>
    </>
  );
};

export default TermsOfService;